import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabase';
import type { Offer, Vendor } from '../lib/database.types';
import { SEATING_STYLES } from '../lib/database.types';
import { formatMoney, formatTime } from '../lib/format';

const DAY_KEYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export default function LocationDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [notFound, setNotFound] = useState(false);

  const todayISO = new Date().toISOString().slice(0, 10);
  const date = searchParams.get('date') || todayISO;
  const headcount = Number(searchParams.get('headcount')) || 10;

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next, { replace: true });
  }

  useEffect(() => {
    if (!slug) return;
    (async () => {
      const { data: v } = await supabase
        .from('ga_vendors')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'active')
        .maybeSingle();
      if (!v) {
        setNotFound(true);
        return;
      }
      setVendor(v as Vendor);
      const { data: o } = await supabase
        .from('ga_offers')
        .select('*')
        .eq('vendor_id', (v as Vendor).id)
        .eq('status', 'active')
        .order('price_cents');
      setOffers((o as Offer[]) ?? []);
    })();
  }, [slug]);

  // Schedule summary is identical across this vendor's offers in practice
  // (they share the bar's hours and group-size window), so surface it once
  // in the address card instead of repeating it on every offer card.
  const schedule = useMemo(() => {
    if (offers.length === 0) return null;
    const first = offers[0];
    return {
      days: first.days_of_week.length > 0 ? first.days_of_week.join(', ') : first.window_days,
      window: `${formatTime(first.window_start)}–${formatTime(first.window_end)}`,
      group: `${first.headcount_min}–${first.headcount_max} ppl`,
    };
  }, [offers]);

  if (notFound) {
    return (
      <>
        <Nav />
        <div style={{ padding: '120px 0', textAlign: 'center' }}>
          <h1 className="display" style={{ fontSize: 64 }}>Not here.</h1>
          <p className="muted">That location isn't on Goaround yet.</p>
          <Link to="/locations" className="btn btn-ghost" style={{ marginTop: 24 }}>← Back to locations</Link>
        </div>
        <Footer />
      </>
    );
  }

  if (!vendor) {
    return (
      <>
        <Nav />
        <div style={{ padding: '120px 0', textAlign: 'center', color: 'var(--ink-soft)' }}>Loading…</div>
      </>
    );
  }

  const selectedDay = DAY_KEYS[new Date(`${date}T00:00:00`).getDay()];

  return (
    <>
      <Nav />
      <section style={{ paddingTop: 32, paddingBottom: 24 }}>
        <div className="container">
          <Link to="/dashboard" className="mono" style={{ fontSize: 12, color: 'var(--ink-soft)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>← All locations</Link>
        </div>
      </section>

      <section style={{ paddingBottom: 32 }}>
        <div className="container">
          <div
            style={{
              width: '100%',
              aspectRatio: '16 / 7',
              borderRadius: 24,
              overflow: 'hidden',
              background: vendor.hero_image_url
                ? `url(${vendor.hero_image_url}) center/cover no-repeat`
                : `linear-gradient(135deg, oklch(0.45 0.14 22), oklch(0.62 0.16 48))`,
            }}
          />
        </div>
      </section>

      <section style={{ paddingBottom: 24 }}>
        <div className="container">
          <div className="ga-loc-pill">
            <div className="ga-loc-pill-cell">
              <span className="ga-loc-pill-label">When</span>
              <input
                type="date"
                value={date}
                min={todayISO}
                onChange={(e) => updateParam('date', e.target.value)}
                aria-label="Date"
              />
            </div>
            <div className="ga-loc-pill-divider" />
            <div className="ga-loc-pill-cell">
              <span className="ga-loc-pill-label">Who</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  type="button"
                  className="ga-loc-stepper"
                  aria-label="Decrease headcount"
                  onClick={() => updateParam('headcount', String(Math.max(1, headcount - 1)))}
                >−</button>
                <span style={{ minWidth: 72, fontWeight: 600 }}>{headcount} people</span>
                <button
                  type="button"
                  className="ga-loc-stepper"
                  aria-label="Increase headcount"
                  onClick={() => updateParam('headcount', String(Math.min(500, headcount + 1)))}
                >+</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ paddingBottom: 48 }}>
        <div className="container">
          <div className="r-split">

            <div>
              <div className="eyebrow" style={{ marginBottom: 14 }}>{vendor.neighborhood}</div>
              <h1 className="display" style={{ fontSize: 'clamp(48px, 7vw, 88px)', margin: 0 }}>{vendor.name}</h1>
              <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {vendor.vibes.map((v) => (
                  <span key={v} className="tag">{v}</span>
                ))}
              </div>
              <p style={{ marginTop: 28, fontSize: 18, lineHeight: 1.55, color: 'var(--ink-soft)', maxWidth: 640 }}>
                {vendor.description}
              </p>
              <div style={{ marginTop: 32, padding: 24, borderRadius: 16, background: 'var(--card)' }}>
                <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 8 }}>Address</div>
                <div style={{ fontSize: 16 }}>{vendor.address}</div>
                {schedule && (
                  <>
                    <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginTop: 16, marginBottom: 8 }}>Open for groups</div>
                    <div style={{ fontSize: 16 }}>{schedule.days} · {schedule.window}</div>
                    <div style={{ fontSize: 14, color: 'var(--ink-soft)', marginTop: 4 }}>Group size {schedule.group}</div>
                  </>
                )}
                {vendor.capacity && (
                  <>
                    <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginTop: 16, marginBottom: 8 }}>Venue capacity</div>
                    <div style={{ fontSize: 16 }}>Up to {vendor.capacity}</div>
                  </>
                )}
              </div>
            </div>

            <div>
              <h2 className="display" style={{ fontSize: 32, margin: '0 0 20px' }}>Reserve your round</h2>
              <div className="stack">
                {offers.length === 0 ? (
                  <p className="muted">No offers active right now. Check back soon.</p>
                ) : (
                  offers.map((o) => {
                    const headcountOk = headcount >= o.headcount_min && headcount <= o.headcount_max;
                    const dayOk = o.days_of_week.length === 0 || o.days_of_week.includes(selectedDay);
                    const available = headcountOk && dayOk;
                    return (
                      <div key={o.id} style={{ padding: 20, borderRadius: 16, border: '1.5px solid var(--ink)', opacity: available ? 1 : 0.62 }}>
                        <div className="row-between">
                          <div className="display" style={{ fontSize: 22 }}>{o.title}</div>
                          <div className="display" style={{ fontSize: 26, color: 'var(--wine)' }}>{formatMoney(o.price_cents)}<span style={{ fontSize: 14, color: 'var(--ink-soft)' }}> /head</span></div>
                        </div>
                        {o.description && <p style={{ color: 'var(--ink-soft)', marginTop: 8, fontSize: 15 }}>{o.description}</p>}
                        <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <span className="tag tag-soft">{SEATING_STYLES.find((s) => s.value === o.seating_style)?.label ?? 'Mixed seating'}</span>
                        </div>
                        <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
                          <OfferLine label="Drinks">
                            <strong>{o.drinks_per_guest}</strong> per guest{o.drinks_included.length > 0 ? <> · {o.drinks_included.join(', ')}</> : null}
                          </OfferLine>
                          {o.food_included && o.food_appetizers_count > 0 && (
                            <OfferLine label="Food">
                              <strong>{o.food_appetizers_count}</strong> shared appetizers per <strong>{o.food_appetizers_per_guests || 4}</strong> people
                              {o.dietary_options.length > 0 && (
                                <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', marginLeft: 6 }}>
                                  {o.dietary_options.map((d) => <span key={d} className="tag tag-soft">{d}</span>)}
                                </span>
                              )}
                            </OfferLine>
                          )}
                          {o.add_ons.length > 0 && (
                            <OfferLine label="Add-ons">
                              <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
                                {o.add_ons.map((a) => (
                                  <span key={a.key} className="tag tag-soft">
                                    {a.label} · +{formatMoney(a.price_cents_per_guest)}/guest
                                  </span>
                                ))}
                              </span>
                            </OfferLine>
                          )}
                          {o.benefits.length > 0 && (
                            <OfferLine label="Extras">
                              {o.benefits.join(' · ')}
                            </OfferLine>
                          )}
                        </div>

                        {!available && (
                          <div className="mono" style={{ marginTop: 14, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--wine)' }}>
                            {!dayOk
                              ? 'Not available on the date you picked'
                              : headcount < o.headcount_min
                                ? `Needs at least ${o.headcount_min} people`
                                : `Caps at ${o.headcount_max} people`}
                          </div>
                        )}

                        <button
                          onClick={() => navigate(`/checkout/${o.id}?date=${encodeURIComponent(date)}&headcount=${headcount}`)}
                          className="btn btn-pop"
                          disabled={!available}
                          style={{ marginTop: 16, width: '100%', justifyContent: 'center' }}
                        >
                          Reserve this round →
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
      <LocationStyles />
    </>
  );
}

function OfferLine({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '72px 1fr', gap: 12, fontSize: 14, color: 'var(--ink)' }}>
      <span className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', paddingTop: 2 }}>{label}</span>
      <span style={{ lineHeight: 1.5 }}>{children}</span>
    </div>
  );
}

function LocationStyles() {
  return (
    <style>{`
      .ga-loc-pill {
        background: var(--bg);
        border: 1.5px solid var(--ink);
        border-radius: 999px;
        padding: 6px 8px 6px 18px;
        display: grid;
        grid-template-columns: 1fr auto 1.3fr;
        align-items: center;
        box-shadow: 0 18px 40px -30px rgba(0,0,0,0.35);
        max-width: 620px;
      }
      .ga-loc-pill-cell {
        padding: 10px 16px;
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }
      .ga-loc-pill-cell input {
        border: none;
        background: transparent;
        padding: 0;
        font-size: 15px;
        font-weight: 600;
        color: var(--ink);
        height: auto;
      }
      .ga-loc-pill-cell input:focus { outline: none; }
      .ga-loc-pill-label {
        font-family: ui-monospace, 'SF Mono', Menlo, monospace;
        font-size: 10px;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-soft);
      }
      .ga-loc-pill-divider {
        width: 1px;
        height: 36px;
        background: var(--line);
      }
      .ga-loc-stepper {
        width: 28px;
        height: 28px;
        border-radius: 999px;
        border: 1.5px solid var(--line);
        background: transparent;
        cursor: pointer;
        font-size: 16px;
        font-weight: 700;
        color: var(--ink);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        transition: border-color 0.15s ease, background 0.15s ease;
      }
      .ga-loc-stepper:hover { border-color: var(--ink); background: var(--card); }

      .tag-soft {
        background: var(--card);
        border: 1px solid var(--line);
        color: var(--ink-soft);
        font-size: 11px;
        padding: 2px 8px;
      }

      @media (max-width: 720px) {
        .ga-loc-pill {
          border-radius: 22px;
          grid-template-columns: 1fr;
          padding: 8px;
        }
        .ga-loc-pill-divider { display: none; }
      }
    `}</style>
  );
}
